import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { AppliedFlashRoomRate } from '../types';
import { applyAiRoomNameOverride, roomNamesMatch } from '../utils';

type FlashRoomRateAssignment = {
  packageData: Record<string, unknown>;
  packageId: string;
};

type RelatedPackageRoom = Pick<OpportunityRelatedDeal, 'id' | 'room_asset' | 'sfid'>;

export type UnmatchedFlashPackage = {
  packageId?: string;
  packageName: string;
  roomAssetName?: string;
};

export function getPackagePersistId(offerPackage: App.Package) {
  const persistId = offerPackage.id_salesforce_external ?? offerPackage.le_package_id;

  return persistId ? String(persistId) : undefined;
}

export function getOfferPackageName(offerPackage: App.Package) {
  return offerPackage.deal_option_name || offerPackage.name || getPackagePersistId(offerPackage) || 'Unknown package';
}

export function getPackageNumberOfNights(offerPackage: App.Package) {
  return 'number_of_nights' in offerPackage && typeof offerPackage.number_of_nights === 'number'
    ? offerPackage.number_of_nights
    : undefined;
}

export function sortPackagesByLengthOfStay(packages: Array<App.Package>) {
  return [...packages].sort((a, b) => {
    const nightsA = getPackageNumberOfNights(a) ?? Number.POSITIVE_INFINITY;
    const nightsB = getPackageNumberOfNights(b) ?? Number.POSITIVE_INFINITY;

    return nightsA - nightsB || getOfferPackageName(a).localeCompare(getOfferPackageName(b));
  });
}

// ApiError's own `message` is often a generic "Validation error" — the field-specific reason
// (e.g. "A flexible nights package requires a max extra nights value") lives in `errors`.
export function getPackageUpdateErrorDetail(error: unknown, fallbackMessage: string) {
  if (
    error &&
    typeof error === 'object' &&
    'errors' in error &&
    Array.isArray(error.errors) &&
    error.errors.length > 0
  ) {
    return error.errors
      .map((issue) => (typeof issue === 'string' ? issue : issue?.message))
      .filter(Boolean)
      .join('; ');
  }

  return fallbackMessage;
}

export function buildFlashPackageRoomRateAssignments({
  aiRoomNameOverrides = {},
  manualRoomTypeIdByPackageId = {},
  offerPackages = [],
  propertyId,
  relatedPackageRooms = [],
  roomRates,
  roomTypes,
}: {
  aiRoomNameOverrides?: Record<string, string>;
  manualRoomTypeIdByPackageId?: Record<string, string>;
  offerPackages?: Array<App.Package>;
  propertyId: string;
  relatedPackageRooms?: Array<RelatedPackageRoom>;
  roomRates: Array<AppliedFlashRoomRate>;
  roomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
}): {
  assignableRoomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
  assignments: Array<FlashRoomRateAssignment>;
  unmatchedPackageNames: Array<string>;
  unmatchedPackages: Array<UnmatchedFlashPackage>;
} {
  const roomRatesByRoomTypeId = new Map<string, Array<AppliedFlashRoomRate>>();

  roomRates.forEach((roomRate) => {
    const existingRoomRates = roomRatesByRoomTypeId.get(roomRate.roomTypeId) ?? [];
    roomRatesByRoomTypeId.set(roomRate.roomTypeId, existingRoomRates.concat(roomRate));
  });

  // Only a room type with an applied rate is assignable, whether matched automatically or picked manually.
  const roomsWithRates = roomTypes.filter((roomType) => roomRatesByRoomTypeId.has(roomType.id));
  const relatedRoomByPackageId = new Map(
    relatedPackageRooms.flatMap((relatedRoom) => {
      const packageId = relatedRoom.sfid || relatedRoom.id;
      return packageId && relatedRoom.room_asset?.name ? [[String(packageId), relatedRoom.room_asset.name]] : [];
    }),
  );

  const assignments: Array<FlashRoomRateAssignment> = [];
  const unmatchedPackageNames: Array<string> = [];
  const unmatchedPackages: Array<UnmatchedFlashPackage> = [];

  offerPackages.forEach((offerPackage) => {
    const packageId = getPackagePersistId(offerPackage);
    const roomAssetName = packageId ? relatedRoomByPackageId.get(packageId) : undefined;
    const manualRoomTypeId = packageId ? manualRoomTypeIdByPackageId[packageId] : undefined;
    // A manual pick is an explicit CM decision, so it takes priority over automatic name matching.
    const roomType = manualRoomTypeId
      ? roomsWithRates.find((roomType) => roomType.id === manualRoomTypeId)
      : roomsWithRates.find((roomType) =>
          roomNamesMatch(roomType.name, applyAiRoomNameOverride(roomAssetName, aiRoomNameOverrides)),
        );
    const roomRatesForRoom = roomType ? (roomRatesByRoomTypeId.get(roomType.id) ?? []) : [];

    if (!packageId || !roomType || roomRatesForRoom.length === 0) {
      const reason = !packageId
        ? 'missing a persist id'
        : !roomAssetName
          ? `no related room found for package id ${packageId}`
          : `no room type named "${roomAssetName}" has an applied rate`;

      unmatchedPackageNames.push(`${getOfferPackageName(offerPackage)} (${reason})`);
      unmatchedPackages.push({ packageId, packageName: getOfferPackageName(offerPackage), roomAssetName });
      return;
    }

    if (roomRatesForRoom.length > 1) {
      assignments.push({
        packageId,
        packageData: {
          fk_property_id: propertyId,
          fk_room_type_id: roomType.id,
        },
      });
      return;
    }

    const [roomRate] = roomRatesForRoom;

    assignments.push({
      packageId,
      packageData: {
        fk_property_id: propertyId,
        fk_room_type_id: roomType.id,
        fk_room_rate_id: roomRate.roomRateId,
      },
    });
  });

  return { assignableRoomTypes: roomsWithRates, assignments, unmatchedPackageNames, unmatchedPackages };
}

export function getMaxExtraNights(pkg: App.Package): number {
  return 'max_extra_nights' in pkg && typeof pkg.max_extra_nights === 'number' ? pkg.max_extra_nights : 0;
}

/** Drops manual package picks pointing at a room that no longer exists (a removed draft, or another property's room). */
export function pruneManualRoomTypeIds(
  manualRoomTypeIdByPackageId: Record<string, string>,
  knownRoomIds: Array<string>,
) {
  const known = new Set(knownRoomIds);

  return Object.fromEntries(Object.entries(manualRoomTypeIdByPackageId).filter(([, roomKey]) => known.has(roomKey)));
}
